import { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import {
    FormAlert,
    PasswordField,
    SelectField,
    SubmitButton,
    TextField,
    UserComboField,
    UserSelect,
    vEmail,
    vRequired,
    type SelectOption,
    type UserPick,
} from '../form';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { get, post, statusOf } from '../../lib/api';
import { ApplicationType } from '../../lib/applications';
import { ModeToggle, type MappingMode } from './ModeToggle';
import { useUserInstances } from './useUserInstances';

/** Map the GetJasperUsers response (UserRoleView) into pickable users. */
const toUserPicks = (
    data: { value: string; firstName?: string; lastName?: string; phoneNumber?: string }[] | undefined,
): UserPick[] =>
    (data ?? []).map((u) => ({
        value: u.value,
        name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
        email: u.value,
        phone: u.phoneNumber ?? '',
    }));

const addSchema = z.object({
    instance: vRequired(),
    orgId: vRequired(),
    ssoUserName: vEmail,
    jasperUser: vRequired(),
    password: vRequired(),
});
type AddValues = z.infer<typeof addSchema>;

const editSchema = z.object({
    orgId: vRequired(),
    jasperUser: vRequired(),
    password: vRequired(),
});
type EditValues = z.infer<typeof editSchema>;

export function JasperMapping() {
    const { t } = useTranslation();
    const [mode, setMode] = useState<MappingMode>('add');
    const [instances, setInstances] = useState<SelectOption[]>([]);
    const [users, setUsers] = useState<UserPick[]>([]);
    const [allUsers, setAllUsers] = useState<SelectOption[]>([]);
    const [serverError, setServerError] = useState('');
    const [success, setSuccess] = useState('');

    const [editUser, setEditUser] = useState('');
    const { userInstances, editInstance, setEditInstance } = useUserInstances(
        mode,
        editUser,
        ApplicationType.Jasper,
    );
    const [loaded, setLoaded] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const addMethods = useForm<AddValues>({
        resolver: zodResolver(addSchema),
        defaultValues: { instance: '', orgId: '', ssoUserName: '', jasperUser: '', password: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });
    const editMethods = useForm<EditValues>({
        resolver: zodResolver(editSchema),
        defaultValues: { orgId: '', jasperUser: '', password: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        post<string[]>('home/getApplicationNames', { appType: ApplicationType.Jasper })
            .then((r) => setInstances((r.data ?? []).map((d) => ({ value: d, label: d })).sort((a, b) => a.label.localeCompare(b.label, 'nb'))))
            .catch(() => setServerError(t('mappings.jasper.instanceLoadError')));
        get<{ value: string; firstName: string; lastName: string; phoneNumber: string }[]>('Home/GetJasperUsers')
            .then((r) => setUsers(toUserPicks(r.data)))
            .catch(() => setServerError(t('mappings.jasper.instanceLoadError')));
        // All SSO users, for the Add form's searchable user picker (Brukernavn). Edit mode uses
        // GetJasperUsers above — only users that already have a Jasper mapping.
        get<{ userName: string; email: string; firstName: string; lastName: string }[]>('Home/GetAllUsers')
            .then((r) =>
                setAllUsers(
                    (r.data ?? [])
                        .map((u) => {
                            const value = u.userName || u.email;
                            const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
                            return { value, label: name ? `${name} (${value})` : value };
                        })
                        .sort((a, b) => a.label.localeCompare(b.label, 'nb')),
                ),
            )
            .catch(() => undefined);
    }, [t]);

    useEffect(() => {
        if (mode !== 'edit' || !editInstance || !editUser) return;
        setSuccess('');
        setServerError('');
        post<{ companyId: string; user: string; userPassword: string }>('Home/LoadSelectedSmartStore', {
            label: editUser,
            value: editInstance,
        })
            .then((res) => {
                editMethods.reset({
                    orgId: res.data.companyId ?? '',
                    jasperUser: res.data.user ?? '',
                    password: res.data.userPassword ?? '',
                });
                setLoaded(true);
            })
            .catch(() => {
                setLoaded(false);
                setServerError(t('mappings.jasper.loadError'));
            });
        // Reload only when the edit selection changes.
    }, [mode, editInstance, editUser]);

    // Clears the edit selections/fields but leaves status messages alone, so
    // the success banner survives the post-update reset.
    function clearEdit() {
        setEditInstance('');
        setEditUser('');
        setLoaded(false);
        editMethods.reset();
    }

    function switchMode(next: MappingMode) {
        setMode(next);
        setServerError('');
        setSuccess('');
        addMethods.reset();
        clearEdit();
    }

    async function onAdd(values: AddValues) {
        setServerError('');
        setSuccess('');
        try {
            await post('home/addJasper', {
                jasperName: values.instance,
                organizationId: values.orgId,
                ssoUserName: values.ssoUserName,
                userName: values.jasperUser,
                password: values.password,
            });
            setSuccess(t('mappings.jasper.added'));
            addMethods.reset();
        } catch (err) {
            setServerError(
                statusOf(err) === 409 ? t('mappings.jasper.mappingExists') : t('mappings.jasper.addError'),
            );
        }
    }

    async function onUpdate(values: EditValues) {
        setServerError('');
        setSuccess('');
        try {
            await post('Home/UpdateJasper', {
                jasperName: editInstance,
                organizationId: values.orgId,
                ssoUserName: editUser,
                userName: values.jasperUser,
                password: values.password,
            });
            setSuccess(t('mappings.jasper.updated'));
            clearEdit();
        } catch {
            setServerError(t('mappings.jasper.updateError'));
        }
    }

    async function confirmDelete() {
        setDeleting(true);
        setServerError('');
        try {
            await post('home/DeleteMapping', { email: editUser, applicationName: editInstance });
            setShowDelete(false);
            setSuccess(t('mappings.deleted'));
            // The user may no longer have a Jasper mapping — refresh the list.
            get<{ value: string; firstName: string; lastName: string; phoneNumber: string }[]>('Home/GetJasperUsers')
                .then((r) => setUsers(toUserPicks(r.data)))
                .catch(() => undefined);
            clearEdit();
        } catch {
            setShowDelete(false);
            setServerError(t('mappings.deleteError'));
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div className="pt-3">
            <p className="text-body-secondary">{t('mappings.jasper.description')}</p>
            <ModeToggle mode={mode} onChange={switchMode} idPrefix="jasper" />

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}
            <FormAlert onClose={() => setServerError('')}>{serverError || undefined}</FormAlert>

            {mode === 'add' ? (
                <FormProvider {...addMethods}>
                    <Form noValidate onSubmit={addMethods.handleSubmit(onAdd)} style={{ maxWidth: '34rem' }}>
                        <SelectField name="instance" label={t('mappings.jasper.instance')} options={instances} required />
                        <UserComboField name="ssoUserName" label={t('mappings.jasper.ssoUserName')} options={allUsers} required />
                        <TextField name="orgId" label={t('mappings.jasper.orgId')} required />
                        <TextField name="jasperUser" label={t('mappings.jasper.jasperUser')} required />
                        <PasswordField name="password" label={t('mappings.jasper.password')} autoComplete="new-password" required />
                        <SubmitButton loading={addMethods.formState.isSubmitting}>
                            {t('mappings.jasper.register')}
                        </SubmitButton>
                    </Form>
                </FormProvider>
            ) : (
                <div style={{ maxWidth: '34rem' }}>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="jasper-edit-user">{t('mappings.jasper.user')}</Form.Label>
                        <UserSelect
                            inputId="jasper-edit-user"
                            users={users}
                            value={editUser}
                            onChange={(v) => {
                                setEditUser(v);
                                setEditInstance('');
                                setLoaded(false);
                            }}
                        />
                    </Form.Group>
                    {userInstances.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="jasper-edit-instance">{t('mappings.jasper.instance')}</Form.Label>
                            <Select
                                inputId="jasper-edit-instance"
                                options={userInstances}
                                classNamePrefix="dkn-rs"
                                placeholder={t('common.selectPlaceholder')}
                                noOptionsMessage={() => t('common.noOptions')}
                                value={userInstances.find((o) => o.value === editInstance) ?? null}
                                onChange={(o) => setEditInstance((o as SelectOption | null)?.value ?? '')}
                            />
                        </Form.Group>
                    )}

                    {loaded && (
                        <FormProvider {...editMethods}>
                            <Form noValidate onSubmit={editMethods.handleSubmit(onUpdate)}>
                                <TextField name="orgId" label={t('mappings.jasper.orgId')} required />
                                <TextField name="jasperUser" label={t('mappings.jasper.jasperUser')} required />
                                <PasswordField name="password" label={t('mappings.jasper.password')} autoComplete="new-password" required />
                                <div className="d-flex gap-2">
                                    <SubmitButton loading={editMethods.formState.isSubmitting}>
                                        {t('mappings.jasper.update')}
                                    </SubmitButton>
                                    <Button
                                        variant="outline-danger"
                                        type="button"
                                        onClick={() => setShowDelete(true)}
                                    >
                                        {t('mappings.delete')}
                                    </Button>
                                </div>
                            </Form>
                        </FormProvider>
                    )}
                </div>
            )}

            <ConfirmDialog
                show={showDelete}
                title={t('mappings.deleteTitle')}
                confirmLabel={t('mappings.proceed')}
                onCancel={() => setShowDelete(false)}
                onConfirm={confirmDelete}
                confirmDisabled={deleting}
            >
                {t('mappings.confirmDelete')}
            </ConfirmDialog>
        </div>
    );
}
